import { Button, Col, Grid, Image, Row, Table, TableProps } from "antd";
import ReceiptModal from "components/Desktop/ReceiptModal";
import { PriceRow } from "components/PriceRow";

import NarinBreadcrumb from "components/Desktop/Breadcrumb";
import NarinLayout from "components/NarinLayout";
import MobileOrderItem from "components/Mobile/MobileOrderItem";
import { E_ORDER_STATUS, IMAGE_NOT_FOUND } from "constants/common";
import HomeIcon from "icons/HomeIcon";
import StarIcon from "icons/StarIcon";
import { Link, useParams } from "react-router-dom";
import { useGetOrderDetail } from "services/order";
import TrackingSection from "./components/TrackingSection";
import CreditCardIcon from "icons/CreditCardIcon";
import PaymentNotificationModal from "components/Desktop/PaymentNotificationModal";
import { useState } from "react";
import { E_PAYMENT_METHOD, PaymentMethodLabels } from "constants/payment";
import { DeliveryMethodLabels, E_DELIVERY_METHOD } from "constants/delivery";
import { IOrder } from "types/order";
import { getImageFullPath } from "utils/image";
import OrderSummaryButton from "components/Desktop/OrderSummaryButton";

interface DataType {
  key: string;
  name: string;
  price: number;
  qty: number;
  url: string;
  isRedemptionItem: boolean;
  redemptionItem?: {
    id: number;
    name: string;
    description: string;
    type: string;
    pointsCost: number;
    cashCost: number;
    images: string[];
  };
  pointsUsed?: number;
  cashUsed?: number;
}

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { orderDetail, isLoading } = useGetOrderDetail(orderId as string);

  // const isPickup = orderDetail?.deliveryMethod !== E_DELIVERY_METHOD.TRANSFER;
  const isPickup =
    orderDetail?.deliveryMethod === E_DELIVERY_METHOD.MAIN_BRANCH ||
    orderDetail?.deliveryMethod === E_DELIVERY_METHOD.RESELL_BRANCH;

  const [isOpenPaymentNotiModal, setIsOpenPaymentNotiModal] =
    useState<boolean>(false);
  const [receiptModalVisible, setReceiptModalVisible] =
    useState<boolean>(false);

  const productList = orderDetail?.orderItems.map((orderItem) => {
    const isRedemptionItem = !!orderItem.redemptionItemId;

    if (isRedemptionItem) {
      return {
        key: `redemption-${orderItem.id}`,
        name: orderItem.redemptionItem?.name || "Redemption Item",
        price: orderItem.priceAtTimeOfOrder,
        qty: orderItem.quantity,
        url: orderItem.redemptionItem?.images?.[0] || "",
        isRedemptionItem: true,
        redemptionItem: orderItem.redemptionItem,
        pointsUsed: orderItem.pointsUsed,
        cashUsed: orderItem.cashUsed,
      };
    } else {
      return {
        key: `product-${orderItem.product?.id}`,
        name: orderItem.product?.name || "Product",
        price: orderItem.priceAtTimeOfOrder,
        qty: orderItem.quantity,
        url: orderItem.product?.images?.[0] || "",
        isRedemptionItem: false,
      };
    }
  }) as DataType[];

  // Separate items into regular products and redemption items
  const regularProducts =
    productList?.filter((item) => !item.isRedemptionItem) || [];
  const redemptionItems =
    productList?.filter((item) => item.isRedemptionItem) || [];

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "",
      dataIndex: "url",
      key: "url",
      width: "12%",
      render: (text: string) => {
        return (
          <Image
            src={`${getImageFullPath(text)}`}
            width={90}
            height={90}
            alt={text}
            className="object-contain"
            fallback={IMAGE_NOT_FOUND}
          />
        );
      },
    },
    {
      title: "สินค้า",
      dataIndex: "name",
      key: "name",
      width: "43%",
    },
    {
      title: "ราคาต่อหน่วย",
      dataIndex: "price",
      key: "price",
      width: "15%",
      render: (text: number, record: DataType) => {
        if (record.isRedemptionItem && record.redemptionItem) {
          return (
            <div>
              <div className="text-purple-600 font-medium">
                {record.redemptionItem.pointsCost.toLocaleString()} คะแนน
              </div>
              {record.redemptionItem.type === "HYBRID" &&
                record.cashUsed &&
                record.cashUsed > 0 && (
                  <div className="text-sm text-gray-600">
                    + {record.cashUsed.toLocaleString()} บาท
                  </div>
                )}
            </div>
          );
        }
        return <div>{text.toLocaleString()} บาท</div>;
      },
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      key: "qty",
      width: "15%",
      render: (text: number) => {
        return <div>{text.toLocaleString()}</div>;
      },
    },
    {
      title: "ราคารวม",
      dataIndex: "sum",
      key: "sum",
      width: "15%",
      render: (_: string, record: DataType) => {
        if (record.isRedemptionItem && record.redemptionItem) {
          return (
            <div>
              <div className="text-purple-600 font-medium">
                {(
                  record.redemptionItem.pointsCost * record.qty
                ).toLocaleString()}{" "}
                คะแนน
              </div>
              {record.redemptionItem.type === "HYBRID" &&
                record.cashUsed &&
                record.cashUsed > 0 && (
                  <div className="text-sm text-gray-600">
                    + {(record.cashUsed * record.qty).toLocaleString()} บาท
                  </div>
                )}
            </div>
          );
        }
        return <div>{(record.qty * record.price).toLocaleString()} บาท</div>;
      },
    },
  ];

  if (isLoading || !orderDetail) {
    return <div></div>;
  }

  const onClosePaymentModal = () => {
    setIsOpenPaymentNotiModal(false);
  };

  const deliveryCost = parseFloat(orderDetail.deliveryCost || "0");

  // Calculate total points used from redemption items
  const totalPointsUsed =
    orderDetail?.orderItems.reduce((total, item) => {
      if (item.redemptionItemId && item.pointsUsed) {
        return total + item.pointsUsed * item.quantity;
      }
      return total;
    }, 0) || 0;

  return (
    <NarinLayout>
      <NarinBreadcrumb
        items={[
          {
            title: "สินค้าทั้งหมด",
            path: "/",
          },
          {
            title: "รายการสั่งซื้อ",
            path: "/setting/order-history",
          },
          {
            title: `สรุปรายการสินค้า #${orderId}`,
          },
        ]}
      />
      <div className="p-4">
        <div className="w-full bg-white">
          <TrackingSection data={orderDetail as IOrder} isPickup={isPickup} />
        </div>
        <div className="w-full bg-white py-8 px-4">
          <div className="flex items-center justify-between">
            <div className="text-base md:text-xl">
              สรุปรายการสินค้า #{orderId}
            </div>
            <OrderSummaryButton
              onClick={() => setReceiptModalVisible(true)}
              className="px-4 md:px-8"
            />
          </div>
          {screens.md ? (
            <div>
              {/* Products Section */}
              {regularProducts.length > 0 && (
                <div className="mb-8">
                  <div className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">🛍️</span>
                    สินค้า ({regularProducts.length})
                  </div>
                  <Table
                    className="table-order-detail"
                    columns={columns}
                    dataSource={regularProducts}
                    pagination={false}
                  />
                </div>
              )}

              {/* Redemption Items Section */}
              {redemptionItems.length > 0 && (
                <div className="mb-8">
                  {regularProducts.length > 0 && (
                    <div className="border-t border-gray-200 my-6"></div>
                  )}
                  <div className="text-lg font-semibold text-purple-600 mb-4 flex items-center">
                    <span className="mr-2">🎁</span>
                    สินค้าแลกคะแนน ({redemptionItems.length})
                  </div>

                  <Table
                    className="table-order-detail"
                    columns={columns}
                    dataSource={redemptionItems}
                    pagination={false}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4 mt-4">
              {/* Products Section */}
              {regularProducts.length > 0 && (
                <div className="mb-6">
                  <div className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                    <span className="mr-2">🛍️</span>
                    สินค้า ({regularProducts.length})
                  </div>
                  {regularProducts.map((item) => (
                    <MobileOrderItem
                      key={item.key}
                      name={item.name}
                      price={item.price}
                      qty={item.qty}
                      url={item.url}
                      isRedemptionItem={item.isRedemptionItem}
                      redemptionItem={item.redemptionItem}
                      pointsUsed={item.pointsUsed}
                      cashUsed={item.cashUsed}
                    />
                  ))}
                </div>
              )}

              {/* Redemption Items Section */}
              {redemptionItems.length > 0 && (
                <div className="mb-6">
                  {regularProducts.length > 0 && (
                    <div className="border-t border-gray-200 my-4"></div>
                  )}
                  <div className="text-lg font-semibold text-purple-600 mb-3 flex items-center">
                    <span className="mr-2">🎁</span>
                    สินค้าแลกคะแนน ({redemptionItems.length})
                  </div>
                  {redemptionItems.map((item) => (
                    <MobileOrderItem
                      key={item.key}
                      name={item.name}
                      price={item.price}
                      qty={item.qty}
                      url={item.url}
                      isRedemptionItem={item.isRedemptionItem}
                      redemptionItem={item.redemptionItem}
                      pointsUsed={item.pointsUsed}
                      cashUsed={item.cashUsed}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="block md:grid md:grid-cols-3 gap-4 mt-8">
          <div className="md:col-span-2 flex flex-col gap-4">
            <div className="bg-white p-4">
              <div className="text-base md:text-xl font-semibold">
                การชำระเงิน
              </div>
              <div>
                <span>รูปแบบการชำระเงิน: </span>
                <span className="font-semibold">
                  {
                    PaymentMethodLabels[
                      orderDetail?.paymentMethod as E_PAYMENT_METHOD
                    ]
                  }
                </span>
              </div>
            </div>
            <div className="bg-white p-4">
              <div className="text-base md:text-xl font-semibold mb-4">
                ข้อมูลการจัดส่ง
              </div>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-y-2 md:gap-4">
                <div className="md:col-span-2 font-medium">
                  สถานที่นัดรับ/จัดส่งสินค้า:{" "}
                </div>
                <div className="md:col-span-5">
                  <pre className="m-0">
                    {
                      DeliveryMethodLabels[
                        orderDetail?.deliveryMethod as E_DELIVERY_METHOD
                      ]
                    }
                  </pre>
                </div>
                <div className="md:col-span-2 font-medium">
                  ชื่อผู้รับสินค้า:{" "}
                </div>
                <div className="md:col-span-5">
                  {orderDetail?.nameOnDelivery}
                </div>
                {isPickup ? (
                  <>
                    <div className="md:col-span-2 font-medium">โทร:</div>
                    <div className="md:col-span-5">
                      {orderDetail?.user?.phoneNumber}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="md:col-span-2 font-medium">
                      ที่อยู่จัดส่ง:{" "}
                    </div>
                    <div className="md:col-span-5">
                      {orderDetail?.deliveryAddress}
                    </div>
                    <div className="md:col-span-2 font-medium">โทร: </div>
                    <div className="md:col-span-5">
                      {orderDetail?.userAddress?.phoneNumber}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="md:col-span-1 bg-white p-4 mt-4 md:mt-0">
            <div className="text-base md:text-lg font-medium mb-4">
              สรุปคำสั่งซื้อ
            </div>
            <Row gutter={16}>
              {orderDetail?.discountAmount ||
              orderDetail?.pointsAsCashDiscount ? (
                <>
                  <PriceRow
                    label="ราคารวม"
                    value={orderDetail?.totalAmount || 0}
                    valueClassName="text-end py-2 px-8 font-semibold text-sm"
                  />
                  {orderDetail?.discountAmount ? (
                    <PriceRow
                      label="ส่วนลดจากโค้ด"
                      value={+orderDetail.discountAmount}
                      isNegative
                    />
                  ) : (
                    <></>
                  )}
                  {orderDetail?.pointsAsCashDiscount > 0 ? (
                    <PriceRow
                      label={`ใช้ ${orderDetail?.pointsAsCashUsed?.toLocaleString()} คะแนน`}
                      value={orderDetail.pointsAsCashDiscount}
                      isNegative
                    />
                  ) : (
                    <></>
                  )}
                  {totalPointsUsed > 0 ? (
                    <PriceRow
                      label="คะแนนที่ใช้แลกสิ่งของ"
                      value={totalPointsUsed}
                      icon={<StarIcon />}
                      isNegative
                    />
                  ) : (
                    <></>
                  )}
                  <PriceRow
                    label="ค่าจัดส่ง"
                    value={
                      orderDetail?.deliveryMethod === E_DELIVERY_METHOD.TRANSFER
                        ? deliveryCost > 0
                          ? deliveryCost
                          : "รอการประเมินราคา"
                        : "ฟรี"
                    }
                    unit={
                      orderDetail?.deliveryMethod ===
                        E_DELIVERY_METHOD.TRANSFER && deliveryCost === 0
                        ? ""
                        : orderDetail?.deliveryMethod ===
                          E_DELIVERY_METHOD.TRANSFER
                        ? "บาท"
                        : ""
                    }
                  />
                  <PriceRow
                    label="คะแนนที่ได้รับจากบิลนี้"
                    value={
                      orderDetail?.status == E_ORDER_STATUS.COMPLETED
                        ? orderDetail?.capsulePointsEarned || 0
                        : Math.floor(+orderDetail?.finalAmount / 25)
                    }
                    icon={<StarIcon />}
                    valueClassName="text-end py-2 px-8 text-sm text-primary font-semibold"
                  />
                  <Col
                    span={12}
                    className="py-2 px-8 text-sm bg-[#f7ffeae8]"
                  ></Col>
                  <Col span={12} className="text-end pt-2 bg-[#f7ffeae8] pr-7">
                    <div className="text-sm">ยอดรวมสุทธิ</div>
                    <div className="text-lg font-semibold">
                      {(
                        +orderDetail?.finalAmount + deliveryCost
                      ).toLocaleString()}{" "}
                      บาท
                    </div>
                  </Col>
                  <Col span={24} className="text-end bg-[#f7ffeae8] pr-7">
                    <div className="text-xs text-success-700">
                      วันนี้คุณประหยัดไปถึง{" "}
                      {(
                        (+orderDetail?.discountAmount || 0) +
                        (orderDetail?.pointsAsCashDiscount || 0)
                      ).toLocaleString()}{" "}
                      บาท
                    </div>
                  </Col>
                </>
              ) : (
                <>
                  <PriceRow
                    label="ราคารวม"
                    value={orderDetail?.totalAmount || 0}
                    valueClassName="text-end py-2 px-8 font-semibold text-sm"
                  />
                  {totalPointsUsed > 0 ? (
                    <PriceRow
                      label="คะแนนที่ใช้แลกสิ่งของ"
                      value={totalPointsUsed}
                      icon={<StarIcon />}
                      isNegative
                    />
                  ) : (
                    <></>
                  )}
                  <PriceRow
                    label="ค่าจัดส่ง"
                    value={
                      orderDetail?.deliveryMethod === E_DELIVERY_METHOD.TRANSFER
                        ? deliveryCost > 0
                          ? deliveryCost
                          : "รอการประเมินราคา"
                        : "ฟรี"
                    }
                    unit={
                      orderDetail?.deliveryMethod ===
                        E_DELIVERY_METHOD.TRANSFER && deliveryCost === 0
                        ? ""
                        : orderDetail?.deliveryMethod ===
                          E_DELIVERY_METHOD.TRANSFER
                        ? "บาท"
                        : ""
                    }
                  />
                  <PriceRow
                    label="คะแนนที่ได้รับจากบิลนี้"
                    value={
                      orderDetail?.status == E_ORDER_STATUS.COMPLETED
                        ? orderDetail?.capsulePointsEarned || 0
                        : Math.floor(+orderDetail?.finalAmount / 25)
                    }
                    icon={<StarIcon />}
                    valueClassName="text-end py-2 px-8 text-sm text-primary font-semibold"
                  />
                  <Col span={12} className="py-2 px-8 text-sm bg-[#f7ffeae8]" />
                  <Col span={12} className="text-end py-2 bg-[#f7ffeae8] pr-7">
                    <div className="text-sm">ยอดรวมสุทธิ</div>
                    <div className="text-lg font-semibold">
                      {(
                        +orderDetail?.finalAmount + deliveryCost
                      ).toLocaleString()}{" "}
                      บาท
                    </div>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </div>
        <div className="flex justify-center gap-4 mt-8 md:mt-12 pb-8 md:pb-12">
          <Link to="/">
            <Button className="px-4 md:px-8" size="large">
              <HomeIcon />
              กลับไปหน้าแรก
            </Button>
          </Link>
          {orderDetail?.status !== E_ORDER_STATUS.COMPLETED &&
          orderDetail?.status !== E_ORDER_STATUS.CANCELLED ? (
            <Button
              className="px-4 md:px-8"
              size="large"
              onClick={() => setIsOpenPaymentNotiModal(true)}
            >
              <CreditCardIcon />
              แจ้งชำระเงิน
            </Button>
          ) : (
            <div />
          )}
        </div>

        {isOpenPaymentNotiModal && (
          <PaymentNotificationModal
            orderNumber={orderDetail?.id}
            isOpen={isOpenPaymentNotiModal}
            onClose={onClosePaymentModal}
          />
        )}

        <ReceiptModal
          isOpen={receiptModalVisible}
          onClose={() => setReceiptModalVisible(false)}
          promotionCode={orderDetail?.promotionUsage?.[0]?.promotionCode?.code}
          productItems={
            orderDetail?.orderItems.map((item) => {
              const isRedemptionItem = !!item.redemptionItemId;

              if (isRedemptionItem) {
                return {
                  id: item.id,
                  productName: item.redemptionItem?.name || "Redemption Item",
                  qty: item.quantity,
                  price: item.cashUsed || 0,
                  isRedemptionItem: true,
                  redemptionItem: item.redemptionItem,
                };
              } else {
                return {
                  id: item.id,
                  productName: item.product?.name || "Product",
                  qty: item.quantity,
                  price: item.priceAtTimeOfOrder,
                  isRedemptionItem: false,
                };
              }
            }) || []
          }
          subtotal={orderDetail?.totalAmount || 0}
          discount={
            (Number(orderDetail?.discountAmount) || 0) +
            (orderDetail?.pointsAsCashDiscount || 0)
          }
          deliveryCost={Number(orderDetail?.deliveryCost) || 0}
          total={
            Number(orderDetail?.finalAmount) +
            Number(orderDetail?.deliveryCost || 0)
          }
          points={
            orderDetail?.status === E_ORDER_STATUS.PENDING ||
            orderDetail?.status === E_ORDER_STATUS.CONFIRMED
              ? Math.floor(Number(orderDetail?.finalAmount) / 25)
              : orderDetail?.capsulePointsEarned || 0
          }
          pointsUsed={totalPointsUsed + (orderDetail?.pointsAsCashUsed || 0)}
          shippingAddress={orderDetail?.deliveryAddress}
          shippingMethod={
            orderDetail?.deliveryMethod
              ? DeliveryMethodLabels[orderDetail.deliveryMethod]
              : ""
          }
          paymentMethod={
            orderDetail?.paymentMethod
              ? PaymentMethodLabels[orderDetail.paymentMethod]
              : ""
          }
        />
      </div>
    </NarinLayout>
  );
};
export default OrderDetailPage;
